import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useLatest } from 'ahooks';
import { useIntl, useLocation } from 'umi';
import Permission from '@/pages/dashboard/components/NoPermission';
import Loading from '@/pages/dashboard/components/Loadings';
import { IPdfReportType, ReportInfoType } from '@home/pages/report/type';
import { usePostLocale } from '@home/hooks/usePostLocale';
import { useIframeAutoSize } from './hooks';

const INIT_REPORT_INFO: ReportInfoType = {
  reportUrl: '',
  noReport: false,
  loading: false,
  errMsg: ''
};

const getIframeSrc = (url?: string, queryParams?: URLSearchParams): string => {
  if (!url) {
    return '';
  }

  if (!queryParams?.toString()) {
    return url;
  }

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url, window.location.origin);
  } catch (e) {
    return url;
  }

  const originalSearch = parsedUrl.search;
  const originalHash = parsedUrl.hash;
  let hasMergedQueryParam = false;

  queryParams?.forEach((value, key) => {
    if (parsedUrl.searchParams.has(key)) {
      return;
    }

    hasMergedQueryParam = true;
    parsedUrl.searchParams.set(key, value);
  });

  if (!hasMergedQueryParam) {
    return url;
  }

  if (parsedUrl.origin === window.location.origin && !url.startsWith(parsedUrl.origin)) {
    const baseUrl = url.replace(`${originalSearch}${originalHash}`, '');
    return `${baseUrl}${parsedUrl.search}${parsedUrl.hash}`;
  }

  return parsedUrl.toString();
};

export const IframeUrl = (props: IPdfReportType) => {
  const {
    inheritQueryParams = false,
    url,
    className = '',
    style = {},
    iframeStyle,
    onSuccess,
    onFail,
    onMessage,
    onLoad,
    autoSize = false,
  } = props || {};
  const { locale } = useIntl();
  const location = useLocation();
  const onMessageLatest = useLatest(onMessage);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [reportInfo, setReportInfo] = useState<ReportInfoType>(INIT_REPORT_INFO);
  const { postLocale } = usePostLocale(iframeRef);
  const queryParams = useMemo(() => {
    if (!inheritQueryParams) {
      return undefined;
    }

    return new URLSearchParams(location.search || '');
  }, [inheritQueryParams, location.search]);

  const setState = (params = {}) => {
    setReportInfo({
      ...INIT_REPORT_INFO,
      ...params
    });
  };

  const appendLocaleToUrl = useCallback((rawUrl: string, targetLocale: string) => {
    if (!rawUrl) return rawUrl;
    const [withoutHash, hash = ''] = rawUrl.split('#');
    const [base, query = ''] = withoutHash.split('?');
    const search = new URLSearchParams(query);
    search.set('locale', targetLocale);
    const queryString = search.toString();
    return `${base}${queryString ? `?${queryString}` : ''}${hash ? `#${hash}` : ''}`;
  }, []);

  useEffect(() => {
    setState({ loading: true });
    if (!url) {
      setState({ noReport: true, loading: false });
      return;
    }
    try {
      // 仅在初始化（url变化）时拼接 locale；后续语言切换通过 postMessage 下发，避免 iframe 刷新
      const reportUrlWithLocale = appendLocaleToUrl(url, locale);
      setState({ reportUrl: reportUrlWithLocale, loading: false, errMsg: '' });
      onSuccess?.();
    } catch (error) {
      setState({ loading: false, errMsg: error });
      onFail?.();
    }
  }, [appendLocaleToUrl, onFail, onSuccess, url]);

  const errorInfo = useMemo(() => {
    // reportUrl 未就绪前不挂载 iframe，避免空 src 触发一次无效 onLoad
    if (reportInfo.loading || (!reportInfo.noReport && !reportInfo.reportUrl)) {
      return <Loading />;
    }
    if (reportInfo.noReport) {
      return <Permission type="error" textCN="未获取到报表信息" />;
    }
    return null;
  }, [reportInfo]);

  useEffect(() => {
    if (!onMessageLatest.current || errorInfo || !reportInfo?.reportUrl) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      const contentWindow = iframeRef.current?.contentWindow;
      if (!contentWindow || event?.source !== contentWindow) {
        return;
      }

      const reportOrigin = new URL(reportInfo.reportUrl, window.location.href).origin;
      if (event?.origin !== reportOrigin) {
        return;
      }

      onMessageLatest.current?.(event);
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [errorInfo, reportInfo?.reportUrl]);

  const hasIframe = !reportInfo.loading && !reportInfo.noReport && !!reportInfo.reportUrl;
  const contentSize = useIframeAutoSize(
    iframeRef,
    autoSize && hasIframe,
    reportInfo?.reportUrl,
  );

  const resolvedStyle = autoSize && contentSize
    ? {
      ...style,
      height: contentSize.height,
      minHeight: contentSize.height,
      maxHeight: 'none',
      overflow: 'visible',
    }
    : style;

  return (
    <>
      {
        errorInfo || <div className={classNames('dtb-db-iframe-wrap', className)} style={resolvedStyle}>
          <iframe
            ref={iframeRef}
            key={reportInfo?.reportUrl}
            className="dtb-db-iframe"
            src={getIframeSrc(reportInfo?.reportUrl, queryParams)}
            style={
              autoSize && contentSize
                ? { ...iframeStyle, height: contentSize.height }
                : iframeStyle
            }
            onLoad={(event) => {
              // 兜底：iframe 完成加载后再下发一次语言，避免首次消息早于子页面监听器注册
              postLocale(locale);
              onLoad?.(event);
            }}
          />
        </div>
      }
    </>
  );
};
